// src/hooks/useSubscription.js - 실시간 Firestore 구독 Hook
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';

const useSubscription = (user, isAdmin = false) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🆕 실시간 Firestore 리스너로 구독 정보 조회
  // 관리자가 권한 부여 시 학생 화면에 즉시 반영됨
  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    // 👑 관리자는 무제한 구독 권한 부여
    if (isAdmin) {
      console.log('👑 관리자 무제한 구독 활성화');
      setSubscription({
        id: 'admin-unlimited',
        planType: 'admin',
        planName: '관리자 (무제한)',
        remainingDownloads: 999999,
        maxDownloads: 999999,
        totalDownloads: 999999,
        expiresAt: null,
        daysRemaining: 999,
        status: 'ACTIVE',
        isExpired: false
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log('📡 실시간 구독 리스너 설정:', user.uid);

    // users 컬렉션 실시간 리스너
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      async (docSnap) => {
        try {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            const userSubscription = userData.subscription;

            // users 컬렉션에 유효한 subscription이 있으면 사용
            // status 체크: 대소문자 무시 (active, ACTIVE, Active 모두 허용)
            const statusLower = userSubscription?.status?.toLowerCase?.() || '';
            const isActiveStatus = statusLower === 'active' || !userSubscription?.status; // status 없으면 활성으로 간주

            console.log('🔍 구독 상태 체크:', {
              hasSubscription: !!userSubscription,
              status: userSubscription?.status,
              statusLower,
              isActiveStatus,
              downloadsRemaining: userSubscription?.downloadsRemaining,
              planType: userSubscription?.planType || userSubscription?.plan
            });

            if (userSubscription && isActiveStatus && userSubscription.downloadsRemaining > 0) {
              console.log('🔄 실시간 구독 정보 업데이트:', userSubscription);

              // 만료일 계산 (expiresAt 또는 endDate 사용)
              let endDate = null;
              if (userSubscription.expiresAt?.toDate) {
                endDate = userSubscription.expiresAt.toDate();
              } else if (userSubscription.expiresAt?._seconds) {
                endDate = new Date(userSubscription.expiresAt._seconds * 1000);
              } else if (userSubscription.expiresAt) {
                endDate = new Date(userSubscription.expiresAt);
              } else if (userSubscription.endDate?._seconds) {
                endDate = new Date(userSubscription.endDate._seconds * 1000);
              } else if (userSubscription.endDate) {
                endDate = new Date(userSubscription.endDate);
              }
              const now = new Date();
              const isExpired = endDate && now > endDate;
              const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 999;

              // planType 또는 plan 필드 사용
              const planType = userSubscription.planType || userSubscription.plan || 'signup_bonus';

              const subscriptionData = {
                id: `user-${user.uid}`,
                planType: planType,
                planName: planType?.toLowerCase() === 'signup_bonus' ? '회원가입 보너스' :
                         planType?.toLowerCase() === 'mock_variation' ? '모의고사 변형 패스' :
                         planType?.toLowerCase() === 'premium' ? '모의고사 변형 패스' :  // 하위호환
                         planType?.toLowerCase() === 'master' ? '올인원 마스터' : planType,
                remainingDownloads: userSubscription.downloadsRemaining || 0,
                maxDownloads: userSubscription.plan === 'signup_bonus' ? 10 : userSubscription.downloadsRemaining,
                totalDownloads: userSubscription.plan === 'signup_bonus' ? 10 : userSubscription.downloadsRemaining,
                expiresAt: endDate ? endDate.toISOString() : null,
                daysRemaining: Math.max(0, daysRemaining),
                status: isExpired ? 'EXPIRED' : 'ACTIVE',
                isExpired: isExpired,
                isBonus: userSubscription.isBonus || false,
                sectionLimits: userSubscription.sectionLimits || {
                  vocabulary: 0,
                  grammar: 0,
                  bluebook_original: 0
                }
              };

              setSubscription(subscriptionData);
              setLoading(false);
              return;
            }
          }

          // users 컬렉션에 구독 정보가 없으면 subscriptions 컬렉션 확인
          const subscriptionsRef = collection(db, 'subscriptions');
          const q = query(
            subscriptionsRef,
            where('userId', '==', user.uid),
            where('status', '==', 'ACTIVE')
          );

          const querySnapshot = await getDocs(q);

          if (querySnapshot.empty) {
            console.log('ℹ️  활성 구독 없음');
            setSubscription(null);
            setLoading(false);
            return;
          }

          // 최신 구독 찾기 (클라이언트 측 정렬)
          const subscriptions = [];
          querySnapshot.forEach((docItem) => {
            subscriptions.push({ id: docItem.id, ...docItem.data() });
          });

          subscriptions.sort((a, b) => {
            const aTime = a.createdAt?.toMillis() || 0;
            const bTime = b.createdAt?.toMillis() || 0;
            return bTime - aTime;
          });

          const latestSub = subscriptions[0];

          // 만료일 확인
          const expiryDate = latestSub.expiryDate?.toDate();
          const now = new Date();
          const isExpired = expiryDate && now > expiryDate;

          if (isExpired) {
            console.log('⚠️  구독이 만료되었습니다');
            setSubscription(null);
            setLoading(false);
            return;
          }

          const daysRemaining = expiryDate
            ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
            : 0;

          // 유료 구독은 다운로드 무제한
          const subscriptionData = {
            id: latestSub.id,
            planType: latestSub.planType,
            planName: latestSub.planType?.toLowerCase() === 'mock_variation' ? '모의고사 변형 패스' :
                     latestSub.planType?.toLowerCase() === 'master' ? '올인원 마스터' : latestSub.planName,
            remainingDownloads: 999999,  // 무제한
            maxDownloads: 999999,
            totalDownloads: 999999,
            isUnlimited: true,  // 무제한 플래그
            expiresAt: expiryDate ? expiryDate.toISOString() : null,
            daysRemaining: daysRemaining,
            status: latestSub.status,
            isExpired: false,
            isBonus: false
          };

          console.log('✅ 유료 구독 정보 로드 완료 (무제한):', subscriptionData);
          setSubscription(subscriptionData);
          setLoading(false);

        } catch (error) {
          console.error('❌ 구독 조회 실패:', error);
          setError(`구독 조회 실패: ${error.message}`);
          setSubscription(null);
          setLoading(false);
        }
      },
      (error) => {
        console.error('❌ 실시간 리스너 오류:', error);
        setError(`구독 리스너 오류: ${error.message}`);
        setLoading(false);
      }
    );

    // 클린업: 컴포넌트 언마운트 시 리스너 해제
    return () => {
      console.log('🔌 구독 리스너 해제');
      unsubscribe();
    };
  }, [user, isAdmin]);

  // PDF 다운로드 (간소화 버전)
  // PDF 다운로드 (간소화 버전)
  const downloadPDF = async (pdfId, pdfTitle = '제목 없음') => {
    if (!subscription) {
      throw new Error('활성 구독이 없습니다.');
    }

    // 관리자는 다운로드 횟수 제한 없음
    if (isAdmin) {
      console.log('👑 관리자 다운로드:', pdfId);

      // 관리자도 다운로드 히스토리 저장
      try {
        await addDoc(collection(db, 'downloadHistory'), {
          userId: user.uid,
          pdfId: pdfId,
          pdfTitle: pdfTitle,
          downloadedAt: serverTimestamp(),
          subscriptionId: 'admin-unlimited',
          userEmail: user.email || '관리자'
        });
        console.log('✅ 관리자 다운로드 히스토리 저장 완료');
      } catch (error) {
        console.error('❌ 다운로드 히스토리 저장 실패:', error);
      }

      return 999999; // 항상 무제한
    }

    console.log('📥 PDF 다운로드:', pdfId);

    // 다운로드 히스토리 저장
    try {
      await addDoc(collection(db, 'downloadHistory'), {
        userId: user.uid,
        pdfId: pdfId,
        pdfTitle: pdfTitle,
        downloadedAt: serverTimestamp(),
        subscriptionId: subscription.id,
        userEmail: user.email || '사용자'
      });
      console.log('✅ 다운로드 히스토리 저장 완료:', pdfTitle);
    } catch (error) {
      console.error('❌ 다운로드 히스토리 저장 실패:', error);
      // 히스토리 저장 실패해도 다운로드는 계속 진행
    }

    // 유료 구독(무제한)인 경우 횟수 차감 없음
    if (subscription.isUnlimited) {
      console.log('✅ 유료 구독 - 무제한 다운로드');
      return 999999;
    }

    // 기존 보너스 사용자: 횟수 확인 및 차감
    if (subscription.remainingDownloads <= 0) {
      throw new Error('다운로드 횟수가 부족합니다.');
    }

    // users 컬렉션의 보너스인 경우 Firestore 업데이트
    if (subscription.isBonus) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          'subscription.downloadsRemaining': subscription.remainingDownloads - 1
        });
        console.log('✅ users 컬렉션 다운로드 횟수 업데이트');
      } catch (error) {
        console.error('❌ 다운로드 횟수 업데이트 실패:', error);
      }

      // 다운로드 횟수 감소 (낙관적 업데이트)
      setSubscription(prev => ({
        ...prev,
        remainingDownloads: prev.remainingDownloads - 1
      }));
    }

    return subscription.remainingDownloads - 1;
  };
  // 구독 새로고침 (결제 완료 후 즉시 갱신용)
  const refreshSubscription = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      console.log('🔄 구독 정보 새로고침 중...', user.uid);

      // 1. 먼저 users 컬렉션에서 회원가입 보너스 확인
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userSubscription = userData.subscription;

        // status 체크: 대소문자 무시
        const statusLower = userSubscription?.status?.toLowerCase?.() || '';
        const isActiveStatus = statusLower === 'active' || !userSubscription?.status;

        if (userSubscription && isActiveStatus && userSubscription.downloadsRemaining > 0) {
          let endDate = null;
          if (userSubscription.expiresAt?.toDate) {
            endDate = userSubscription.expiresAt.toDate();
          } else if (userSubscription.expiresAt?._seconds) {
            endDate = new Date(userSubscription.expiresAt._seconds * 1000);
          } else if (userSubscription.expiresAt) {
            endDate = new Date(userSubscription.expiresAt);
          } else if (userSubscription.endDate?._seconds) {
            endDate = new Date(userSubscription.endDate._seconds * 1000);
          } else if (userSubscription.endDate) {
            endDate = new Date(userSubscription.endDate);
          }
          const now = new Date();
          const isExpired = endDate && now > endDate;
          const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : 999;
          const planType = userSubscription.planType || userSubscription.plan || 'signup_bonus';

          const subscriptionData = {
            id: `user-${user.uid}`,
            planType: planType,
            planName: planType?.toLowerCase() === 'signup_bonus' ? '회원가입 보너스' :
                     planType?.toLowerCase() === 'premium' ? '프리미엄' :
                     planType?.toLowerCase() === 'master' ? '마스터' : planType,
            remainingDownloads: userSubscription.downloadsRemaining || 0,
            maxDownloads: userSubscription.plan === 'signup_bonus' ? 10 : userSubscription.downloadsRemaining,
            totalDownloads: userSubscription.plan === 'signup_bonus' ? 10 : userSubscription.downloadsRemaining,
            expiresAt: endDate ? endDate.toISOString() : null,
            daysRemaining: Math.max(0, daysRemaining),
            status: isExpired ? 'EXPIRED' : 'ACTIVE',
            isExpired: isExpired,
            isBonus: userSubscription.isBonus || false,
            sectionLimits: userSubscription.sectionLimits || {
              vocabulary: 0,
              grammar: 0,
              bluebook_original: 0
            }
          };

          setSubscription(subscriptionData);
          console.log('✅ 구독 정보 새로고침 완료 (users):', subscriptionData);
          return;
        }
      }

      // 2. subscriptions 컬렉션에서 활성 구독 조회
      const subscriptionsRef = collection(db, 'subscriptions');
      const q = query(
        subscriptionsRef,
        where('userId', '==', user.uid),
        where('status', '==', 'ACTIVE')
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('ℹ️ 활성 구독 없음');
        setSubscription(null);
        return;
      }

      const subscriptions = [];
      querySnapshot.forEach((docSnap) => {
        subscriptions.push({ id: docSnap.id, ...docSnap.data() });
      });

      subscriptions.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      const latestSub = subscriptions[0];
      const expiryDate = latestSub.expiryDate?.toDate();
      const now = new Date();
      const isExpired = expiryDate && now > expiryDate;

      if (isExpired) {
        console.log('⚠️ 구독이 만료되었습니다');
        setSubscription(null);
        return;
      }

      const daysRemaining = expiryDate
        ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
        : 0;

      // 유료 구독은 다운로드 무제한
      const subscriptionData = {
        id: latestSub.id,
        planType: latestSub.planType,
        planName: latestSub.planType?.toLowerCase() === 'mock_variation' ? '모의고사 변형 패스' :
                 latestSub.planType?.toLowerCase() === 'master' ? '올인원 마스터' : latestSub.planName,
        remainingDownloads: 999999,  // 무제한
        maxDownloads: 999999,
        totalDownloads: 999999,
        isUnlimited: true,  // 무제한 플래그
        expiresAt: expiryDate ? expiryDate.toISOString() : null,
        daysRemaining: daysRemaining,
        status: latestSub.status,
        isExpired: false,
        isBonus: false
      };

      console.log('✅ 유료 구독 정보 새로고침 완료 (무제한):', subscriptionData);
      setSubscription(subscriptionData);

    } catch (err) {
      console.error('❌ 구독 새로고침 실패:', err);
      setError(`구독 새로고침 실패: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 헬퍼 함수들
  const canDownload = () => {
    // 관리자는 항상 다운로드 가능
    if (isAdmin) return true;
    // 유료 구독(무제한)이면 만료 여부만 확인
    if (subscription?.isUnlimited) {
      return subscription && !subscription.isExpired;
    }
    // 기존 보너스 사용자는 횟수도 확인
    return subscription && subscription.remainingDownloads > 0 && !subscription.isExpired;
  };

  // 카테고리별 다운로드 권한 확인
  // - mock_variation 플랜: mock_variation 카테고리만 다운로드 가능
  // - master 플랜: 모든 카테고리 다운로드 가능
  const canDownloadCategory = (category) => {
    // 관리자는 모든 카테고리 접근 가능
    if (isAdmin) return true;

    // 구독 없거나 만료되었으면 불가
    if (!subscription || subscription.isExpired) {
      return false;
    }

    // 유료 구독(무제한)이 아닌 경우 횟수 확인
    if (!subscription.isUnlimited && subscription.remainingDownloads <= 0) {
      return false;
    }

    const planType = subscription.planType?.toLowerCase() || '';

    // 플랜별 접근 가능 카테고리
    const categoryAccess = {
      'mock_variation': ['mock_variation'],  // 모의고사 변형 패스: 모의고사만
      'mock': ['mock_variation'],             // orderId 파싱 시 MOCK만 추출되는 경우 대응
      'premium': ['mock_variation'],          // 하위호환: premium도 모의고사만
      'master': ['vocabulary', 'grammar', 'bluebook_original', 'listening', 'mock_variation'],  // 올인원: 전체
      'signup_bonus': ['vocabulary', 'grammar', 'bluebook_original', 'listening', 'mock_variation'],  // 가입보너스: 전체
      'admin': ['vocabulary', 'grammar', 'bluebook_original', 'listening', 'mock_variation']  // 관리자: 전체
    };

    const allowedCategories = categoryAccess[planType] || [];
    return allowedCategories.includes(category);
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return 'NONE';
    if (subscription.isExpired) return 'EXPIRED';
    // 유료 구독(무제한)은 횟수 체크 안함
    if (!subscription.isUnlimited && subscription.remainingDownloads <= 0) return 'DEPLETED';
    return 'ACTIVE';
  };

  const getStatusMessage = () => {
    if (error) return `오류: ${error}`;

    const status = getSubscriptionStatus();

    switch (status) {
      case 'NONE':
        return '구독 플랜이 필요합니다.';
      case 'EXPIRED':
        return '구독이 만료되었습니다.';
      case 'DEPLETED':
        return '다운로드 횟수를 모두 사용했습니다.';
      case 'ACTIVE':
        // 유료 구독(무제한)
        if (subscription.isUnlimited) {
          return '다운로드 무제한';
        }
        // 기존 보너스 사용자
        return `${subscription.remainingDownloads}회 다운로드 가능`;
      default:
        return '구독 상태를 확인할 수 없습니다.';
    }
  };

  const getDaysRemaining = () => {
    if (!subscription || !subscription.expiresAt) return 0;

    const expiryDate = new Date(subscription.expiresAt);
    const now = new Date();
    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    return Math.max(0, daysRemaining);
  };

  const getUsagePercentage = () => {
    if (!subscription || !subscription.maxDownloads) return 0;
    const used = subscription.maxDownloads - subscription.remainingDownloads;
    return Math.round((used / subscription.maxDownloads) * 100);
  };

  return {
    subscription,
    loading,
    error,
    downloadPDF,
    refreshSubscription,
    canDownload,
    canDownloadCategory,
    getSubscriptionStatus,
    getStatusMessage,
    getDaysRemaining,
    getUsagePercentage,
    isActive: getSubscriptionStatus() === 'ACTIVE',
    isExpired: getSubscriptionStatus() === 'EXPIRED',
    hasSubscription: !!subscription,
    downloadsRemaining: subscription?.remainingDownloads || 0,
    totalDownloads: subscription?.maxDownloads || 0,
    planName: subscription?.planType || '',
    planType: subscription?.planType || ''
  };
};

export default useSubscription;
